import { LoadingButton } from '@mui/lab';
import { Button, Tooltip, Typography } from '@mui/material';
import { IconX, IconEye, IconEyeOff } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import InputComponent from 'components/Input';
import InputSelected from 'components/InputSelected';
import { Formik } from 'formik';
import { get, post, put } from 'lib/network/api';
import { errorMsg, successMsg } from 'lib/toastNotification';
import React, { useState } from 'react';

const deliveryMethod = {
  EMAIL: 'email',
  SFTP: 'sftp',
  BOTH: 'both',
};
const ConfigurationTabs = () => {
  const [showPassword, setShowPassword] = useState(false);
  const queryClient = useQueryClient();
  const initialValues = {
    deliveryMethod: {
      id: 0,
      name: 'email',
      value: deliveryMethod.EMAIL,
    },
    emailRecipients: [],
    sftpConfiguration: {
      sftp_delivery_enabled: false,
      sftp_host: '',
      sftp_port: 22,
      sftp_username: '',
      sftp_password: '',
    },
  };
  const dataOption = [
    {
      id: 0,
      name: 'email',
      value: deliveryMethod.EMAIL,
    },
    {
      id: 1,
      name: 'SFTP',
      value: deliveryMethod.SFTP,
    },
    {
      id: 0,
      name: 'both',
      value: deliveryMethod.BOTH,
    },
  ];

  const mutationConfiguration = useMutation({
    mutationFn: (payload) => {
      return put('/notification-configurations', payload);
    },
    onSuccess: () => {
      successMsg('Configuration updated successfully');
    },
    onError: (error) => {
      errorMsg(error.detail);
    },
  });

  const handleOnSubmit = async (values, action) => {
    let payload = {};
    if (values.deliveryMethod.value === deliveryMethod.EMAIL) {
      if (values.emailRecipients.length === 0) {
        errorMsg('Please add at least one email recipient.');
        return;
      }
      mutationConfiguration.mutate({
        email_recipients: values.emailRecipients.join(','),
        email_delivery_enabled: true,
      });
    } else if (values.deliveryMethod.value === deliveryMethod.SFTP) {
      const port = Number(values.sftpConfiguration.sftp_host.split(':')[1]) || 22;
      if (
        !values.sftpConfiguration.sftp_host ||
        !values.sftpConfiguration.sftp_username ||
        !values.sftpConfiguration.sftp_password
      ) {
        errorMsg('Please fill in all SFTP fields.');
        return;
      }
      mutationConfiguration.mutate({
        email_recipients: '',
        email_delivery_enabled: false,
        sftp_delivery_enabled: true,
        sftp_host: values.sftpConfiguration.sftp_host.split(':')[0],
        sftp_port: port,
        sftp_username: values.sftpConfiguration.sftp_username,
        sftp_password: values.sftpConfiguration.sftp_password,
      });
    } else {
      const port = Number(values.sftpConfiguration.sftp_host.split(':')[1]) || 22;
      payload = {
        email_recipients: values.emailRecipients.join(','),
        email_delivery_enabled: false,
        sftp_delivery_enabled: false,
        sftp_host: values.sftpConfiguration.sftp_host.split(':')[0],
        sftp_port: port,
        sftp_username: values.sftpConfiguration.sftp_username,
        sftp_password: values.sftpConfiguration.sftp_password,
      };
    }
    mutationConfiguration.mutate(payload, {
      onSuccess: () => {
        successMsg('Configuration updated successfully');
        queryClient.invalidateQueries(['configuration']);
        action.resetForm();
      },
    });
  };

  const mutationTestConnection = useMutation({
    mutationFn: (payload) => {
      return post('/notification-configurations/test-sftp-connection', payload);
    },
    onSuccess: () => {
      successMsg('SFTP connection test successful');
    },
    onError: (error) => {
      errorMsg(error.detail);
    },
  });

  const handleTestConnection = async (values) => {
    if (
      !values.sftpConfiguration.sftp_host ||
      !values.sftpConfiguration.sftp_username ||
      !values.sftpConfiguration.sftp_password
    ) {
      return errorMsg('Please fill in all SFTP fields before testing the connection.');
    }
    const payload = {
      sftp_host: values.sftpConfiguration.sftp_host.split(':')[0],
      sftp_port: Number(values.sftpConfiguration.sftp_host.split(':')[1]) || 22,
      sftp_username: values.sftpConfiguration.sftp_username,
      sftp_password: values.sftpConfiguration.sftp_password,
    };

    mutationTestConnection.mutate(payload);
  };

  const handleAddEmailRecipient = (values, setFieldValue) => {
    if (!values.email) {
      return errorMsg('Please enter an email address.');
    }
    const email = values.email.trim();
    if (!email) {
      return errorMsg('Email cannot be empty.');
    }
    if (values.emailRecipients.includes(email)) {
      return errorMsg('This email is already added.');
    }
    if (values.emailRecipients.length >= 2) {
      return errorMsg('You can only add up to 2 email recipients.');
    }
    setFieldValue('emailRecipients', [...values.emailRecipients, email]);
    setFieldValue('email', '');
  };

  const { data: dataConfiguration } = useQuery({
    queryKey: ['configuration'],
    queryFn: async () => {
      const res = await get('/notification-configurations');
      if (res) {
        const emailRecipients = res.email_recipients ? res.email_recipients.split(',') : [];
        return {
          ...res,
          deliveryMethod: {
            id: 0,
            name: res.email_delivery_enabled ? 'email' : 'sftp',
            value: res.email_delivery_enabled ? deliveryMethod.EMAIL : deliveryMethod.SFTP,
          },
          emailRecipients,
          sftpConfiguration: {
            sftp_host: res.sftp_host || '',
            sftp_port: res.sftp_port || 22,
            sftp_username: res.sftp_username || '',
            sftp_password: res.sftp_password || '',
          },
        };
      }
    },
  });

  return (
    <div className="rounded-lg border p-6">
      <div className="flex flex-col gap-y-1">
        <h4 className="text-gray-900 font-semibold text-lg">Report Delivery Configuration</h4>
        <span className="text-gray-400">
          Configure how reports are delivered to your systems. Choose between Email, SFTP, or both
          delivery methods.
        </span>
      </div>
      <div className="mt-8">
        <Formik
          enableReinitialize={true}
          initialValues={dataConfiguration ?? initialValues}
          onSubmit={(values, action) => handleOnSubmit(values, action)}>
          {({ handleSubmit, setFieldValue, values, errors, touched }) => (
            <div>
              <h4 className="text-gray-900 font-semibold text-base">Settlement Report</h4>
              <div className="flex flex-col gap-y-3 mt-3">
                <div>
                  <InputSelected
                    label="Delivery Method"
                    dataOption={dataOption}
                    width="max-w-md w-full"
                    selected={values.deliveryMethod}
                    setSelected={(val) => {
                      setFieldValue('deliveryMethod', val);
                    }}
                  />
                </div>
                {values.deliveryMethod.value === deliveryMethod.EMAIL ||
                values.deliveryMethod.value === deliveryMethod.BOTH ? (
                  <div className="space-y-1">
                    <div className="flex items-center text-gray-900 gap-x-2">
                      <span className="font-medium">Email Recipients</span>
                      <Tooltip
                        title="Max 2 email recipients"
                        placement="top-start"
                        arrow
                        slotProps={{
                          popper: {
                            modifiers: [
                              {
                                name: 'offset',
                                options: {
                                  offset: [0, -5],
                                },
                              },
                            ],
                          },
                          tooltip: {
                            sx: {
                              color: '#111827',
                              backgroundColor: '#ffff',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              shadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                              dropShadow: '0px 1px 2px rgba(0, 0, 0, 0.06)',
                            },
                          },
                          arrow: {
                            sx: {
                              '&:before': {
                                border: '1px solid #d1d5db',
                              },
                              color: '#ffff',
                            },
                          },
                        }}>
                        <div className="w-4 h-4 rounded-full border border-gray-400 text-center flex items-center justify-center text-xs">
                          i
                        </div>
                      </Tooltip>
                    </div>
                    <div className="flex items-end gap-x-2  text-gray-500">
                      <InputComponent
                        name="email"
                        type="email"
                        placeholder="Enter email address"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddEmailRecipient(values, setFieldValue);
                          }
                        }}
                      />
                      <Button
                        variant="outlined"
                        sx={{ borderColor: '#cbcfd5', color: '#6b7280' }}
                        onClick={() => handleAddEmailRecipient(values, setFieldValue)}>
                        Add
                      </Button>
                    </div>
                    <p className="text-sm text-gray-400">
                      {values?.emailRecipients?.length}/2 recipients added
                    </p>
                    {values?.emailRecipients?.length >= 1 ? (
                      <div className="space-y-2">
                        <p className="text-gray-900 font-medium text-sm mt-5">Recipients:</p>
                        <div className="space-y-1">
                          {values.emailRecipients.map((email, index) => (
                            <div
                              key={index}
                              className="bg-gray-200 text-gray-900 font-medium rounded-md p-2 flex items-center justify-between">
                              <span>{email}</span>
                              <IconX
                                className="cursor-pointer w-4 h-4"
                                onClick={() => {
                                  const newRecipients = values.emailRecipients.filter(
                                    (item) => item !== email,
                                  );
                                  setFieldValue('emailRecipients', newRecipients);
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <></>
                    )}
                  </div>
                ) : (
                  <></>
                )}
                {values.deliveryMethod.value === deliveryMethod.SFTP ||
                values.deliveryMethod.value === deliveryMethod.BOTH ? (
                  <div className="p-4 border bg-gray-100 rounded-lg">
                    <Typography variant="h6" className="text-gray-900">
                      SFTP Configuration
                    </Typography>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                      <InputComponent
                        label="SFTP Host:Port"
                        name="sftpConfiguration.sftp_host"
                        type="text"
                        placeholder="sftp.example.com:22"
                      />
                      <InputComponent
                        label="SFTP Username"
                        name="sftpConfiguration.sftp_username"
                        type="text"
                        placeholder="username"
                      />
                      <InputComponent
                        name="sftpConfiguration.sftp_password"
                        type={`${showPassword ? 'text' : 'password'}`}
                        label="SFTP Password"
                        placeholder="password"
                        icon={
                          showPassword ? (
                            <IconEyeOff className=" text-primary-main h-5 w-5" />
                          ) : (
                            <IconEye className="text-gray-400 h-5 w-5" />
                          )
                        }
                        clickicon={() => setShowPassword(!showPassword)}
                      />

                      <div className="flex items-end ">
                        <LoadingButton
                          variant="contained"
                          size="small"
                          className="w-full h-9"
                          loading={mutationTestConnection.isPending}
                          onClick={() => handleTestConnection(values)}>
                          Test Connection
                        </LoadingButton>
                      </div>
                    </div>
                  </div>
                ) : (
                  <></>
                )}
              </div>
              <div className="flex flex-col lg:flex-row items-center justify-between mt-8 gap-y-4">
                {/* <LoadingButton
                  variant="outlined"
                  size="small"
                  className="hover:!bg-info-main hover:text-white w-full lg:w-fit">
                  Reset to Default
                </LoadingButton> */}
                <div className="flex flex-col lg:flex-row items-center gap-x-2 w-full lg:w-fit">
                  <LoadingButton
                    className=" w-full lg:w-fit"
                    variant="contained"
                    size="small"
                    type="submit"
                    onClick={handleSubmit}
                    loading={mutationConfiguration.isPending}>
                    Save Changes
                  </LoadingButton>
                </div>
              </div>
            </div>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default ConfigurationTabs;
